<template>
	<canvas id='chartCanvas' />
</template>


<script>
import { animatePoints } from '../js/animate.js';
import { scaleLinear } from 'd3-scale';

export default {
	props: {
		rawData: Array,
		config: Object,
		isPortrait: Boolean,
		screenWidth: Number,
		screenHeight: Number
	},
	data() {
		return {
			pointsPerRow: 50,
			pointWidth: 4,
			points: [],
			xRange: [-73.981159, -73.949722],
			yRange: [40.764911, 40.800119],
			height: 0,
			width: 0,
			margin: {
				top: 20,
				right: 20,
				bottom: 20,
				left: 20
			},
			axes: []
		}
	},
	watch: {
		screenWidth() {
			this.resize();
			this.updateAxes();
			this.updateCanvas();
		},
		screenHeight() {
			this.resize();
			this.updateAxes();
			this.updateCanvas();
		},
		config() {
			this.updateCanvas();
		},
		rawData() {
			// create points array
			let arr = [];
			this.rawData.forEach(pt => {
				arr.push(pt);
			})
			this.points = arr;

			// set initial state
			const config = {'layout': 'map', 'color': 'gray', 'action': 'reveal'};
			this.updateAxes();
			this.updatePoints(config);
		}
	},
	methods: {
		resize() {
			// square on landscape; cover on portrait
			const el = document.querySelector('#chartCanvas');
			const parent = document.querySelector('#chart-wrapper')
			const dims = parent.getBoundingClientRect();

			const height = dims.height

			// resize the canvas
			el.height = height;
			el.width = this.isPortrait ? dims.width : height;

			// store sizes for plotting
			this.height = el.height;
			this.width = el.width;
		},
		updateCanvas() {
			const config = this.config;
			let pt = {};

			const action = Object.prototype.hasOwnProperty.call(config, 'action') ? config.action : 'none';
			switch (action ) {
				case 'clear':
					animatePoints([], action, 0, []);
					break;

				case 'first':
					this.updatePoints(config);
					pt = this.points.filter(x => { return x.id === '22D-PM-1012-01' });
					animatePoints(pt, action, config.duration);
					break;

				case 'height':
					this.updatePoints(config);
					animatePoints(this.points, action, config.duration, this.axes)
					break;

				default:
					this.updatePoints(config);
					animatePoints(this.points, action, config.duration);
					break;
			}
		},
		updatePoints(config) {
			// store old layout
			this.points.forEach(point => {
				point.x0 = point.x;
				point.y0 = point.y;
				point.color0 = point.color;
			});

			// sort points if necessary
			const sort = Object.prototype.hasOwnProperty.call(config, 'sort') ? config.sort : '';
			switch (sort) {
				case 'color':
					this.sortPointsColor();
					break;
				case 'age':
					this.sortPointsAge();
					break;
				case 'height':
					this.sortPointsHeight();
					break;
			}

			// get new layout
			const layout = Object.prototype.hasOwnProperty.call(config, 'layout') ? config.layout : ''; 
			switch (layout) {
				case 'map':
					this.setPointsLayoutMap();
					break;
				case 'grid': 
					this.setPointsLayoutGrid();
					break;
				case 'height': 
					this.setPointsLayoutHeight();
					break;
			}

			// get new colors
			const color = Object.prototype.hasOwnProperty.call(config, 'color') ? config.color: '';
			switch (color) {
				case 'gray':
					this.points.forEach(point => {
						point.color = 'rgb(30, 30, 30)';
					})
					break;
				case 'age':
					this.setPointsColorAge();
					break;
				case 'fur':
					this.setPointsColorFur();
					break;
				case 'lowest':
					this.setPointsColorHeight('lowest');
					break;
				case 'highest':
					this.setPointsColorHeight('highest')
					break;
				case 'first':
					this.setPointsColorFirst();
					break;
			}
		},
		updateAxes() {
			const pts = [0, 10, 20, 30, 50, 100, 180];
			let recs = []
			pts.forEach(pt => {
				recs.push({
					x: this.margin.right,
					y: this.heightY(pt),
					label: pt
				});
			});
			this.axes = recs;
		},
		setPointsColorFur() {
			this.points.forEach(point => {
				let newColor = '';
				switch (point.color_primary) {
					case 'Black':
						newColor = 'rgb(30, 30, 30)';
						break;
					case 'Gray':
						newColor = 'rgb(150, 150, 150)';
						break;
					case "Cinnamon":
						newColor = 'rgb(130, 75, 55)';
						break;
					default:
						newColor = 'rgb(255, 255, 255)';
						break;
				}
				point.color = newColor;
			});
		},
		setPointsColorAge() {
			this.points.forEach(point => {
				let color = '';
				switch (point.age) {
					case 'Adult':
						color = 'rgb(180, 190, 210)';
						break;
					case 'Juvenile':
						color = 'rgb(105, 125, 160)';
						break;
					default:
						color = 'rgb(30, 30, 30)';
						break;
				}
				point.color = color;
			});
		},
		setPointsColorHeight(type) {
			this.points.forEach(point => {
				const ht = point.height;

				// set colors
				const light_green = 'rgb(185, 195, 170)';
				const dark_green = 'rgb(120, 130, 100)';
				const light_red = 'rgb(220, 220, 220)';
				const dark_red = 'rgb(230, 0, 0)';

				if (type === 'lowest') {
					point.color = ht === 0 ? dark_green : light_green;
				} 

				if (type === 'highest') {
					point.color = ht === 180 ? dark_red : light_red;
				} 
			});
		},
		setPointsLayoutMap() {
			this.points.forEach(point => {
				point.x = this.mapX(point.lon);
				point.y = this.mapY(point.lat);
			});
		},
		setPointsLayoutGrid() {
			// space b/n grid cells
			const spacer = 1.1;

			// center align this thing
			const canvasWidth = this.width;
			const canvasHeight = this.height;

			const gridWidth = this.pointWidth*spacer*(this.pointsPerRow-1);
			const gridHeight = this.pointWidth*spacer*Math.floor(this.points.length / this.pointsPerRow);

			// calculate position
			const xStart = (canvasWidth - gridWidth)/2;
			const yStart = (canvasHeight - gridHeight)/2;

			this.points.forEach((point, i) => {
				point.x = xStart + this.pointWidth*spacer * (i % this.pointsPerRow);
				point.y = yStart + this.pointWidth*spacer * Math.floor(i / this.pointsPerRow);
			});
		},
		setPointsLayoutHeight() {
			// FIGURE OUT X PLACEMENT
			// get a list of unique heights
			const heights = this.points.map(x => x.height);
			const uniqHeights = [...new Set(heights)]; 

			// make a dict with keys for each uniqHeight
			let grps = {};
			for (let i=0; i<uniqHeights.length; i++) { grps[uniqHeights[i]] = 0 }
			
			// find out how many squirrels are in each
			this.points.forEach(pt => {
				grps[pt.height]++;
				pt.heightID = grps[pt.height];
			});

			// SET THE COORDS
			// set the points layout
			this.points.forEach(point => {
				point.x = this.heightX(point.heightID);
				point.y = this.heightY(point.height);
			});
		},
		sortPointsColor() {
			let sorted = [];
			const colors = ['Gray', 'Cinnamon', 'Black', 'none'];
			for (let c=0; c<colors.length; c++) {
				const color = colors[c];
				for (let p=0; p<this.points.length; p++) {
					const pt = this.points[p];
					if (pt.color_primary === color) {
						sorted.push(pt);
					}
				}
			}
			this.points = sorted;
		},
		sortPointsAge() {
			let sorted = [];
			const ages = ['Juvenile', 'Adult', 'none'];
			for (let a=0; a<ages.length; a++) {
				const age = ages[a];
				for (let p=0; p<this.points.length; p++) {
					const pt = this.points[p];
					if (pt.age === age) {
						sorted.push(pt);
					}
				}
			}
			this.points = sorted;
		},
		sortPointsHeight() {
			this.points.sort(function(a, b){return b.height - a.height});
		},
		mapX(val) {
			var scale = scaleLinear()
				.domain(this.xRange)
				.range([0 + this.margin.left, this.width - this.margin.right]);
			return scale(val)
		},		
		mapY(val) {
			var scale = scaleLinear()
				.domain(this.yRange)
				.range([this.height - this.margin.bottom, 0 + this.margin.top]);
			return scale(val)
		},
		heightX(val) {
			var scale = scaleLinear()
				.domain([0, 2231])
				.range([0 + this.margin.left*2, this.width - this.margin.right]);
			return scale(val)
		},
		heightY(val) {
			var scale = scaleLinear()
				.domain([-5, 185])
				.range([this.height - this.margin.bottom, 0 + this.margin.top]);
			return scale(val)
		}
	}
}

</script>

<style>
</style>